import React, { useEffect, useState } from "react";
import { ReactComponent as EyeOpen } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeClosed } from "../../assets/icons/eye-slash.svg";
import { Link, useNavigate } from "react-router-dom";

const API_BASE = "https://api.logvi-havox.com";

function useIsMobile(breakpoint: number = 980): boolean {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const update = () => setIsMobile(mq.matches);

    update();

    if (mq.addEventListener) {
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    }

    // @ts-ignore
    mq.addListener(update);

    // @ts-ignore
    return () => mq.removeListener(update);
  }, [breakpoint]);

  return isMobile;
}

type FeedbackState = {
  type: "ok" | "err";
  text: string;
};

const CadastroContainer: React.FC = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [codigo, setCodigo] = useState("");
  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<FeedbackState | null>(null);

  function onlyDigits(value: string) {
    return value.replace(/\D+/g, "");
  }

  function validate(): string | null {
    if (!codigo.trim()) return "Informe o código de acesso.";
    if (!nome.trim()) return "Informe o seu nome completo.";
    if (!email.trim() || !email.includes("@")) {
      return "Informe um e-mail válido.";
    }
    if (senha.length < 8) return "A senha deve ter pelo menos 8 caracteres.";

    return null;
  }

  async function handleRegister() {
    if (submitting) return;

    setMsg(null);

    const validationError = validate();

    if (validationError) {
      setMsg({ type: "err", text: validationError });
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/auth/register-hml`, {
        method: "POST",
        credentials: "include",
        cache: "no-store",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          nome: nome.trim(),
          email: email.trim().toLowerCase(),
          senha,
          codigo: codigo.trim(),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data?.ok === false) {
        const reason =
          data?.erro ||
          data?.message ||
          `Falha ao cadastrar. HTTP ${res.status}.`;

        throw new Error(reason);
      }

      setMsg({
        type: "ok",
        text: "Cadastro realizado com sucesso. Você já pode acessar sua conta.",
      });

      setCodigo("");
      setNome("");
      setEmail("");
      setSenha("");
      setShowPassword(false);

      setTimeout(() => {
        navigate("/entrar", { replace: true });
      }, 900);
    } catch (error: any) {
      setMsg({
        type: "err",
        text: error?.message || "Não foi possível concluir o cadastro.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  function onEnterSubmit(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRegister();
    }
  }

  const pageStyle: React.CSSProperties = {
    minHeight: "100vh",
    width: "100%",
    position: "relative",
    overflow: "hidden",
    background:
      "linear-gradient(135deg, #061B20 0%, #08272E 38%, #0B333B 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "86px 14px 18px" : "28px 24px",
    boxSizing: "border-box",
    fontFamily: "Poppins, sans-serif",
  };

  const backgroundLayerStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
  };

  const gridOverlayStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    opacity: 0.22,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
    backgroundSize: "42px 42px",
    maskImage:
      "linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)",
    WebkitMaskImage:
      "linear-gradient(90deg, transparent 0%, #000 18%, #000 82%, transparent 100%)",
  };

  const radialLightStyle: React.CSSProperties = {
    position: "absolute",
    width: isMobile ? 420 : 720,
    height: isMobile ? 420 : 720,
    borderRadius: "50%",
    top: isMobile ? -170 : -260,
    right: isMobile ? -220 : -260,
    background:
      "radial-gradient(circle, rgba(0,192,183,0.22) 0%, rgba(0,192,183,0.10) 32%, rgba(0,192,183,0) 68%)",
  };

  const radialDarkStyle: React.CSSProperties = {
    position: "absolute",
    width: isMobile ? 520 : 760,
    height: isMobile ? 520 : 760,
    borderRadius: "50%",
    bottom: isMobile ? -300 : -360,
    left: isMobile ? -260 : -280,
    background:
      "radial-gradient(circle, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.12) 34%, rgba(0,0,0,0) 70%)",
  };

  const diagonalLineOneStyle: React.CSSProperties = {
    position: "absolute",
    width: "72%",
    height: 1,
    left: "-10%",
    top: "27%",
    transform: "rotate(-18deg)",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 44%, transparent 100%)",
  };

  const diagonalLineTwoStyle: React.CSSProperties = {
    position: "absolute",
    width: "64%",
    height: 1,
    right: "-14%",
    bottom: "25%",
    transform: "rotate(-18deg)",
    background:
      "linear-gradient(90deg, transparent 0%, rgba(0,192,183,0.18) 45%, transparent 100%)",
  };

  const smallAccentOneStyle: React.CSSProperties = {
    position: "absolute",
    width: 68,
    height: 68,
    right: isMobile ? 22 : "20%",
    top: isMobile ? 84 : "15%",
    borderRadius: 18,
    transform: "rotate(18deg)",
    border: "1px solid rgba(0,192,183,0.20)",
    background: "rgba(0,192,183,0.045)",
  };

  const smallAccentTwoStyle: React.CSSProperties = {
    position: "absolute",
    width: 84,
    height: 84,
    left: isMobile ? 18 : "18%",
    bottom: isMobile ? 44 : "16%",
    borderRadius: 22,
    transform: "rotate(-18deg)",
    border: "1px solid rgba(255,255,255,0.11)",
    background: "rgba(255,255,255,0.025)",
  };

  const cardStyle: React.CSSProperties = {
    width: "100%",
    maxWidth: isMobile ? 360 : 392,
    borderRadius: 14,
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.985) 0%, rgba(248,250,252,0.975) 100%)",
    border: "1px solid rgba(255,255,255,0.42)",
    padding: isMobile ? "18px 14px 14px" : "22px 22px 18px",
    boxSizing: "border-box",
    zIndex: 2,
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow:
      "0 28px 70px rgba(0,0,0,0.28), 0 1px 0 rgba(255,255,255,0.78) inset",
    position: "relative",
    overflow: "hidden",
  };

  const cardAccentStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: 3,
    background: "linear-gradient(90deg, #0E323A 0%, #00C0B7 50%, #0E323A 100%)",
  };

  const formTitleStyle: React.CSSProperties = {
    margin: "0 0 5px",
    color: "#0F172A",
    fontSize: isMobile ? 22 : 24,
    lineHeight: 1.12,
    fontWeight: 760,
    letterSpacing: "-0.025em",
  };

  const subtitleStyle: React.CSSProperties = {
    margin: "0 0 16px",
    color: "#64748B",
    fontSize: 12,
    lineHeight: 1.45,
    fontWeight: 500,
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: 11.5,
    fontWeight: 700,
    color: "#334155",
    marginBottom: 6,
  };

  const fieldGroupStyle: React.CSSProperties = {
    width: "100%",
    marginBottom: 11,
  };

  const inputWrapStyle = (field: string): React.CSSProperties => {
    const focused = focusedField === field;

    return {
      width: "100%",
      minHeight: 44,
      borderRadius: 8,
      border: focused ? "1px solid #0E323A" : "1px solid #D8E0E8",
      background: focused ? "#FFFFFF" : "#F8FAFC",
      display: "flex",
      alignItems: "center",
      padding: "0 12px",
      boxSizing: "border-box",
      position: "relative",
      transition:
        "border-color .16s ease, background .16s ease, box-shadow .16s ease",
      boxShadow: focused ? "0 0 0 3px rgba(14, 50, 58, 0.08)" : "none",
    };
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    minHeight: 42,
    border: "none",
    outline: "none",
    background: "transparent",
    color: "#0F172A",
    fontSize: 13,
    fontWeight: 550,
    fontFamily: "Poppins, sans-serif",
    paddingRight: 36,
    boxSizing: "border-box",
  };

  const eyeButtonStyle: React.CSSProperties = {
    position: "absolute",
    right: 8,
    top: "50%",
    transform: "translateY(-50%)",
    width: 30,
    height: 30,
    borderRadius: 7,
    border: "none",
    background: "transparent",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: "#64748B",
    padding: 0,
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: "100%",
    minHeight: 44,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.08)",
    cursor: submitting ? "not-allowed" : "pointer",
    background: "linear-gradient(180deg, #0E323A 0%, #09262D 100%)",
    color: "#FFFFFF",
    fontFamily: "Poppins, sans-serif",
    fontSize: 13.5,
    fontWeight: 750,
    letterSpacing: 0.15,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform .16s ease, box-shadow .16s ease, opacity .16s ease",
    marginTop: 2,
    marginBottom: 12,
    opacity: submitting ? 0.78 : 1,
    boxShadow: "0 12px 22px rgba(14, 50, 58, 0.20)",
  };

  const feedbackStyle = (type: "ok" | "err"): React.CSSProperties => ({
    width: "100%",
    marginBottom: 12,
    borderRadius: 8,
    padding: "9px 10px",
    fontSize: 11.5,
    fontWeight: 650,
    lineHeight: 1.45,
    border:
      type === "err"
        ? "1px solid rgba(220, 38, 38, 0.14)"
        : "1px solid rgba(22, 163, 74, 0.14)",
    background:
      type === "err" ? "rgba(254, 242, 242, 1)" : "rgba(240, 253, 244, 1)",
    color: type === "err" ? "#B91C1C" : "#15803D",
    boxSizing: "border-box",
  });

  const bottomRowStyle: React.CSSProperties = {
    marginTop: 2,
    paddingTop: 12,
    borderTop: "1px solid rgba(148, 163, 184, 0.18)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexWrap: "wrap",
  };

  const mutedTextStyle: React.CSSProperties = {
    color: "#64748B",
    fontSize: 11.5,
    lineHeight: 1.45,
  };

  const linkStyle: React.CSSProperties = {
    color: "#0E323A",
    fontWeight: 750,
    textDecoration: "none",
  };

  const helperStyle: React.CSSProperties = {
    marginTop: -5,
    marginBottom: 11,
    color: "#94A3B8",
    fontSize: 10.5,
    lineHeight: 1.4,
    fontWeight: 500,
  };

  return (
    <div style={pageStyle}>
      <div style={backgroundLayerStyle}>
        <div style={gridOverlayStyle} />
        <div style={radialLightStyle} />
        <div style={radialDarkStyle} />
        <div style={diagonalLineOneStyle} />
        <div style={diagonalLineTwoStyle} />
        <div style={smallAccentOneStyle} />
        <div style={smallAccentTwoStyle} />
      </div>

      <div style={cardStyle}>
        <div style={cardAccentStyle} />

        <div style={formTitleStyle}>Criar acesso</div>

        <div style={subtitleStyle}>
          Preencha os dados abaixo para solicitar seu primeiro acesso ao Logvi.
        </div>

        {msg && (
          <div style={feedbackStyle(msg.type)} role="alert">
            {msg.text}
          </div>
        )}

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Código de acesso</label>

          <div style={inputWrapStyle("codigo")}>
            <input
              style={inputStyle}
              type="password"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={codigo}
              onFocus={() => setFocusedField("codigo")}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => {
                const value = onlyDigits(e.target.value).slice(0, 10);
                setCodigo(value);
              }}
              onKeyDown={onEnterSubmit}
              placeholder="Informe o código recebido"
            />
          </div>
        </div>

        <div style={helperStyle}>
          O código define a empresa, o departamento e as permissões iniciais.
        </div>

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Nome completo</label>

          <div style={inputWrapStyle("nome")}>
            <input
              style={inputStyle}
              type="text"
              value={nome}
              onFocus={() => setFocusedField("nome")}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => setNome(e.target.value)}
              onKeyDown={onEnterSubmit}
              placeholder="Seu nome completo"
              autoComplete="name"
            />
          </div>
        </div>

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>E-mail corporativo</label>

          <div style={inputWrapStyle("email")}>
            <input
              style={inputStyle}
              type="email"
              value={email}
              onFocus={() => setFocusedField("email")}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onEnterSubmit}
              placeholder="seunome@dominio.com.br"
              autoComplete="email"
            />
          </div>
        </div>

        <div style={fieldGroupStyle}>
          <label style={labelStyle}>Senha</label>

          <div style={inputWrapStyle("senha")}>
            <input
              style={inputStyle}
              type={showPassword ? "text" : "password"}
              value={senha}
              onFocus={() => setFocusedField("senha")}
              onBlur={() => setFocusedField(null)}
              onChange={(e) => setSenha(e.target.value)}
              onKeyDown={onEnterSubmit}
              placeholder="Mínimo de 8 caracteres"
              autoComplete="new-password"
            />

            <button
              type="button"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              title={showPassword ? "Ocultar senha" : "Mostrar senha"}
              style={eyeButtonStyle}
            >
              {showPassword ? <EyeClosed /> : <EyeOpen />}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={handleRegister}
          disabled={submitting}
          style={primaryButtonStyle}
          onMouseEnter={(e) => {
            if (submitting) return;

            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow =
              "0 16px 28px rgba(14, 50, 58, 0.26)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 12px 22px rgba(14, 50, 58, 0.20)";
          }}
        >
          {submitting ? "Criando acesso..." : "Criar acesso"}
        </button>

        <div style={bottomRowStyle}>
          <div style={mutedTextStyle}>
            Já possui uma conta?{" "}
            <Link to="/entrar" style={linkStyle}>
              Acesse agora
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastroContainer;
